import { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChecklistWidget from "@/components/ChecklistWidget";
import { StepsCard, YouCard, ClientCard } from "@/components/PracticeBlocks";
import mermaid from "mermaid";
import { Copy, Check } from "lucide-react";

// Lazy-init Mermaid once on first import.
let mermaidReady = false;
function ensureMermaid(isDark) {
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: "loose",
    // Mermaid v10+ renders a "bomb" SVG on parse failure instead of throwing.
    // This flag makes it throw instead so our try/catch can fall back gracefully.
    suppressErrorRendering: true,
    theme: isDark ? "dark" : "default",
    themeVariables: {
      primaryColor: "#FFB400",
      primaryTextColor: isDark ? "#f5f4ef" : "#0a0a0a",
      primaryBorderColor: "#d69600",
      lineColor: isDark ? "#94a3a0" : "#0a0a0a",
      secondaryColor: "#0288D1",
      tertiaryColor: "#1565C0",
      fontFamily: "'Manrope', system-ui, sans-serif",
      fontSize: "14px",
    },
  });
  mermaidReady = true;
}

// Some upstream Markdown sources emit Mermaid with smart punctuation
// (curly quotes, Unicode arrows, em-dashes, parentheses inside axis labels)
// that the strict Mermaid parser rejects. This sanitizer normalises the
// most common cases so the diagram still renders, without changing semantics.
function sanitizeMermaid(src) {
  let s = src;
  // Normalise Unicode arrows to ASCII (but don't touch existing `-->` / `<--`)
  s = s.replace(/[→⇒➔➜➞➝➡⟶]/g, "-->");
  s = s.replace(/[←⟵]/g, "<--");
  s = s.replace(/[↔⟷]/g, "<-->");
  // Em/en dashes to ASCII hyphens
  s = s.replace(/[–—]/g, "-");
  // Smart quotes to straight quotes
  s = s.replace(/[“”„«»]/g, '"').replace(/[‘’‚‹›]/g, "'");
  // Non-breaking spaces to regular spaces
  s = s.replace(/\u00a0/g, " ");
  // Tabs -> 2 spaces (mindmap is whitespace-sensitive)
  s = s.replace(/\t/g, "  ");
  // Strip stray markdown backticks that sometimes leak inside diagrams
  s = s.replace(/`/g, "");

  // Strip a stray colon right after `title` / `x-axis` / `y-axis` / `quadrant-N`
  // ( e.g. `x-axis: Low --> High` -> `x-axis Low --> High` )
  s = s.replace(
    /^(\s*(?:title|x-axis|y-axis|quadrant-[1-4]))\s*:\s*/gim,
    "$1 "
  );

  // If an x-axis / y-axis line is missing `-->`, try to infer one from
  // common natural phrasings ( "Low to High", "Low vs High" ).
  s = s.replace(
    /^(\s*(?:x-axis|y-axis)\s+)([^\n]+)$/gim,
    (full, head, body) => {
      if (/-->|<--/.test(body)) return full;
      const m = body.match(/^(.+?)\s+(?:to|vs\.?|versus)\s+(.+)$/i);
      if (m) return `${head}${m[1].trim()} --> ${m[2].trim()}`;
      return full;
    }
  );

  // Strip parentheses / brackets inside `x-axis` / `y-axis` / `quadrant-N`
  // header lines — Mermaid's quadrantChart lexer rejects them.
  s = s.replace(
    /^(\s*(?:x-axis|y-axis|quadrant-[1-4])\s+[^\n]*)$/gim,
    (line) => line.replace(/[()[\]{}]/g, "")
  );

  // For quadrantChart data points like `Some (parenthesized) label: [0.3, 0.4]`,
  // strip parentheses from the label and wrap it in double quotes so any
  // remaining punctuation (commas, &, /) is accepted by the parser.
  s = s.replace(
    /^(\s*)("?)([^\n:"]+?)\2(\s*:\s*\[\s*[\d.]+\s*,\s*[\d.]+\s*\])\s*$/gm,
    (_full, indent, _q, label, rest) => {
      const clean = label.replace(/[()[\]{}]/g, "").trim();
      return `${indent}"${clean}"${rest}`;
    }
  );

  return s;
}

function plainText(t) {
  return t.replace(/\*\*(.*?)\*\*/g, "$1").replace(/__(.*?)__/g, "$1").replace(/^#{1,6}\s+/gm, "").replace(/^\s*[-*]\s+/gm, "");
}

function CodeBlock({ className, text, ...props }) {
  const [copied, setCopied] = useState(false);
  // ```text = paste-ready prose card (headline, About, post, note…); ```banner = dark LinkedIn banner card.
  const isText = /language-(text|txt|plain)\b/.test(className || "");
  const isBanner = /language-banner\b/.test(className || "");
  const isAsk = /language-ask\b/.test(className || "");
  const shown = isText || isBanner || isAsk ? plainText(text) : text;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shown);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shown;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  if (isAsk) {
    // ```ask = the GPT needs something from the participant: shown as a loud "Your turn" card so it can't be missed.
    const lines = shown.split("\n").filter((l) => l.trim());
    return (
      <div className="markdown-ask-card" data-testid="ask-block" role="status">
        <span className="markdown-ask-label">Your turn — I need this from you</span>
        {lines.map((l, i) => <p key={i} className={`markdown-ask-line${i === 0 ? " is-lead" : ""}`}>{l}</p>)}
      </div>
    );
  }
  if (isBanner) {
    const [headline, ...rest] = shown.split("\n").filter((l) => l.trim());
    return (
      <div className="markdown-banner-card" data-testid="banner-block">
        <button type="button" className={`markdown-copy-btn ${copied ? "is-copied" : ""}`} onClick={copy} aria-label="Copy" data-testid="copy-code-button">
          {copied ? <Check size={13} /> : <Copy size={13} />}<span>{copied ? "Copied" : "Copy"}</span>
        </button>
        <p className="markdown-banner-headline">{headline}</p>
        {rest.map((l, i) => <p key={i} className="markdown-banner-line">{l}</p>)}
      </div>
    );
  }
  return (
    <div className={`markdown-code-wrap${isText ? " is-text" : ""}`} data-testid="code-block">
      {isText && <span className="markdown-text-label">Ready to paste</span>}
      <button
        type="button"
        className={`markdown-copy-btn ${copied ? "is-copied" : ""}`}
        onClick={copy}
        aria-label={isText ? "Copy" : "Copy code"}
        data-testid="copy-code-button"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        <span>{copied ? "Copied" : isText ? "Copy" : "Copy code"}</span>
      </button>
      <pre className="markdown-pre">
        <code className={className} {...props}>
          {shown}
        </code>
      </pre>
    </div>
  );
}


function MermaidBlock({ code, isDark, onZoom }) {
  const ref = useRef(null);
  const [error, setError] = useState(null);
  const [svg, setSvg] = useState("");

  useEffect(() => {
    if (!mermaidReady) ensureMermaid(isDark);
    let cancelled = false;
    const baseId = `mmd-${Math.random().toString(36).slice(2, 10)}`;
    const cleaned = sanitizeMermaid(code);

    (async () => {
      // 1. Parse-first with suppressErrors so a bad diagram never produces
      //    Mermaid's default "bomb" SVG. Try sanitized, then raw as a fallback.
      let useSource = null;
      try {
        const parsedCleaned = await mermaid.parse(cleaned, { suppressErrors: true });
        if (parsedCleaned) {
          useSource = cleaned;
        } else {
          const parsedRaw = await mermaid.parse(code, { suppressErrors: true });
          if (parsedRaw) useSource = code;
        }
      } catch (_) {
        useSource = null;
      }

      if (!useSource) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn("[Mermaid] unparseable diagram", { sanitized: cleaned, original: code });
          setError("Could not parse diagram syntax");
          setSvg("");
        }
        return;
      }

      try {
        const { svg: rendered } = await mermaid.render(`${baseId}`, useSource);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn("[Mermaid] render failed after successful parse", e);
          setError(String(e?.message || e));
          setSvg("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, isDark]);

  useEffect(() => {
    if (svg && ref.current) ref.current.innerHTML = svg;
  }, [svg]);

  if (error) {
    return (
      <pre className="markdown-code-fallback">
        {code}
        <div className="markdown-mermaid-error">
          Could not render diagram — showing source instead.
        </div>
      </pre>
    );
  }
  return (
    <button
      type="button"
      className="markdown-mermaid-zoomable"
      onClick={() => svg && onZoom && onZoom(svg)}
      aria-label="Zoom diagram"
      title="Click to zoom"
    >
      <div className="markdown-mermaid" ref={ref} />
    </button>
  );
}

// Fullscreen lightbox with blurred backdrop. Closes on ESC or backdrop click.
function Lightbox({ content, kind, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="markdown-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="diagram-lightbox"
    >
      <button
        type="button"
        className="markdown-lightbox-close"
        onClick={onClose}
        aria-label="Close zoom"
        data-testid="diagram-lightbox-close"
      >
        ×
      </button>
      <div
        className="markdown-lightbox-stage"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === "svg" ? (
          <div
            className="markdown-lightbox-svg"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <img src={content} alt="" className="markdown-lightbox-img" />
        )}
      </div>
    </div>
  );
}

export default function MarkdownMessage({ content, isDark = false, storageKey }) {
  const [zoom, setZoom] = useState(null); // { kind: 'svg'|'img', content: string }
  const openSvgZoom = useCallback((svg) => setZoom({ kind: "svg", content: svg }), []);
  const openImgZoom = useCallback((src) => setZoom({ kind: "img", content: src }), []);

  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // react-markdown v10 no longer passes `inline`; fenced blocks arrive as
          // <pre><code>, so handle blocks in `pre` and treat `code` as inline.
          pre({ children }) {
            const child = Array.isArray(children) ? children[0] : children;
            const cp = child?.props || {};
            const match = /language-(\w+)/.exec(cp.className || "");
            const text = String(cp.children ?? "").replace(/\n$/, "");
            if (match?.[1] === "mermaid") {
              return <MermaidBlock code={text} isDark={isDark} onZoom={openSvgZoom} />;
            }
            if (match?.[1] === "steps") return <StepsCard text={text} />;
            if (match?.[1] === "you") return <YouCard text={text} />;
            if (match?.[1] === "client") return <ClientCard text={text} />;
            if (match?.[1] === "next") return null; // quick-reply label, rendered as a button by Chat.jsx
            return <CodeBlock className={cp.className} text={text} />;
          },
          code({ className, children, ...props }) {
            return (
              <code className="markdown-inline-code" {...props}>
                {children}
              </code>
            );
          },
          img({ src, alt, ...props }) {
            return (
              <button
                type="button"
                className="markdown-img-zoomable"
                onClick={() => src && openImgZoom(src)}
                aria-label="Zoom image"
                title="Click to zoom"
              >
                <img src={src} alt={alt || ""} {...props} />
              </button>
            );
          },
          table({ children }) {
            return (
              <div className="markdown-table-wrap">
                <table className="markdown-table">{children}</table>
              </div>
            );
          },
          blockquote({ children }) {
            return <blockquote className="markdown-blockquote">{children}</blockquote>;
          },
          ul({ node, children, ...props }) {
            const cls = node?.properties?.className;
            const isTaskList = Array.isArray(cls) ? cls.includes("contains-task-list") : String(cls || "").includes("contains-task-list");
            if (isTaskList) return <ChecklistWidget node={node} storageKey={storageKey} />;
            return <ul {...props}>{children}</ul>;
          },
          a({ children, ...props }) {
            return (
              <a
                {...props}
                target="_blank"
                rel="noopener noreferrer"
                className="markdown-link"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
      {zoom && (
        <Lightbox kind={zoom.kind} content={zoom.content} onClose={() => setZoom(null)} />
      )}
    </div>
  );
}
